import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Brain, Camera, CreditCard, Shield, Zap } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Features = () => {
  const { t } = useLanguage();

  const features = t('features.items');
  const icons = [Smartphone, Brain, Camera, CreditCard];
  const additionalIcons = [Shield, Zap];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section 
      id="features" 
      data-testid="features-section"
      className="py-24 md:py-32 relative overflow-hidden bg-[#0A0A0A]"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #FF7A00 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Glow Effects */}
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#FF7A00]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-sm tracking-widest uppercase mb-4">
            Technology
          </span>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t('features.title')}
          </h2>
          <p className="text-lg text-white/60 max-w-xl mx-auto">
            {t('features.subtitle')}
          </p>
        </motion.div>

        {/* Features Grid - Bento Style */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6"
        >
          {/* Large Feature Card - Mobile & Cloud */}
          <motion.div
            variants={itemVariants}
            data-testid="feature-card-0"
            className="lg:col-span-7 glass-card rounded-3xl p-8 md:p-10 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF7A00]/10 rounded-full blur-[100px] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex items-center justify-center mb-6">
                <Smartphone className="w-7 h-7 text-[#FF7A00]" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">{features[0].title}</h3>
              <p className="text-white/60 text-lg leading-relaxed max-w-md">{features[0].description}</p>
              
              {/* Visual Element */}
              <div className="mt-8 flex items-center gap-4">
                <div className="flex -space-x-2">
                  <div className="w-10 h-10 rounded-full bg-[#FF7A00]/20 border-2 border-[#050505] flex items-center justify-center">
                    <span className="text-xs font-bold text-[#FF7A00]">iOS</span>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-[#FF7A00]/20 border-2 border-[#050505] flex items-center justify-center">
                    <span className="text-xs font-bold text-[#FF7A00]">And</span>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-[#FF7A00]/20 border-2 border-[#050505] flex items-center justify-center">
                    <span className="text-xs font-bold text-[#FF7A00]">Web</span>
                  </div>
                </div>
                <span className="text-sm text-white/40">Multi-platform</span>
              </div>
            </div>
          </motion.div>

          {/* AI Feature Card */}
          <motion.div
            variants={itemVariants}
            data-testid="feature-card-1"
            className="lg:col-span-5 glass-card rounded-3xl p-8 relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF7A00]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex items-center justify-center mb-6">
                <Brain className="w-7 h-7 text-[#FF7A00]" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-3">{features[1].title}</h3>
              <p className="text-white/60 leading-relaxed">{features[1].description}</p>
              
              {/* AI Badge */}
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                <span className="text-sm text-white/70">Amazon Rekognition</span>
              </div>
            </div>
          </motion.div>

          {/* Quality Feature Card */}
          <motion.div
            variants={itemVariants}
            data-testid="feature-card-2"
            className="lg:col-span-5 glass-card rounded-3xl p-8 relative overflow-hidden group"
          >
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex items-center justify-center mb-6">
                <Camera className="w-7 h-7 text-[#FF7A00]" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-3">{features[2].title}</h3>
              <p className="text-white/60 leading-relaxed">{features[2].description}</p>
              
              {/* HD Badge */}
              <div className="mt-6 flex items-center gap-3">
                <div className="px-3 py-1 rounded-lg bg-[#FF7A00]/10 text-[#FF7A00] text-sm font-medium">HD</div>
                <div className="px-3 py-1 rounded-lg bg-[#FF7A00]/10 text-[#FF7A00] text-sm font-medium">4K</div>
                <div className="px-3 py-1 rounded-lg bg-[#FF7A00]/10 text-[#FF7A00] text-sm font-medium">RAW</div>
              </div>
            </div>
          </motion.div>

          {/* Payment Feature Card */}
          <motion.div
            variants={itemVariants}
            data-testid="feature-card-3"
            className="lg:col-span-7 glass-card rounded-3xl p-8 md:p-10 relative overflow-hidden group"
          >
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#FF7A00]/10 rounded-full blur-[100px] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            
            <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <div className="w-14 h-14 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex items-center justify-center mb-6">
                  <CreditCard className="w-7 h-7 text-[#FF7A00]" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-3">{features[3].title}</h3>
                <p className="text-white/60 leading-relaxed">{features[3].description}</p>
              </div>
              
              {/* Payment Icons */}
              <div className="flex flex-wrap gap-3">
                {['Stripe', 'Apple Pay', 'Google Pay'].map((method, i) => (
                  <div key={i} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white/70">
                    {method}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom Row - Security & Performance */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div className="glass-card rounded-2xl p-6 flex items-center gap-4" data-testid="feature-security">
            <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 border border-[#10B981]/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#10B981]" />
            </div>
            <div>
              <h4 className="font-semibold text-white">Cloud Sécurisé</h4>
              <p className="text-sm text-white/50">Stockage conforme aux normes de sécurité</p>
            </div>
          </div>
          <div className="glass-card rounded-2xl p-6 flex items-center gap-4" data-testid="feature-performance">
            <div className="w-12 h-12 rounded-xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#FF7A00]" />
            </div>
            <div>
              <h4 className="font-semibold text-white">Traitement Instantané</h4>
              <p className="text-sm text-white/50">Reconnaissance faciale en quelques secondes</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
